import { isIntakeFormStatus, type IntakeFormStatus } from "@/lib/fulfillment/intakeFormStatus";
import { isMfgUnitOrigin, type MfgUnitOrigin } from "@/lib/fulfillment/originEnum";

export type SalesDeskLotUnit = {
  id: string;
  display_label: string;
  serial: string | null;
  model: string;
  origin: string;
  notes: string;
  intake_form_status: IntakeFormStatus | null;
  salePathLabel: string;
};

export type SalesDeskShownEvent = {
  unitId: string;
  at: string;
};

const ORIGIN_LABEL: Record<MfgUnitOrigin, string> = {
  manufactured: "Manufactured",
  cash_purchase: "Cash purchase",
  repo_intake: "Repo intake",
};

const INTAKE_LABEL: Record<IntakeFormStatus, string> = {
  pending: "Intake needs filling",
  awaiting_review: "Intake needs review",
  work_in_progress: "Intake in progress",
  approved: "Intake approved",
};

export function salesDeskLotDisplayLabel(serial: string | null, id: string): string {
  const trimmed = serial?.trim() ?? "";
  return trimmed || `pre-build:${id.slice(0, 8)}`;
}

export function salesDeskLotSalePathLabel(salePath: string): string {
  if (salePath === "inventory_no_sale_yet") return "Not sold yet";
  if (salePath === "cash") return "Cash";
  if (salePath === "finance") return "Finance";
  if (salePath === "rto") return "RTO";
  return salePath;
}

export function salesDeskLotSaleNameLabel(saleName: string): string {
  const name = saleName.trim();
  if (!name) return "";
  if (/rto/i.test(name)) return "RTO";
  if (/cash/i.test(name)) return "Cash";
  if (/finance/i.test(name)) return "Finance";
  return salesDeskLotSalePathLabel(name);
}

export function salesDeskLotOriginLabel(origin: string): string {
  return isMfgUnitOrigin(origin) ? ORIGIN_LABEL[origin] : origin;
}

export function salesDeskLotIntakeFromStatus(status: unknown): IntakeFormStatus | null {
  return isIntakeFormStatus(status) ? status : null;
}

export function salesDeskLotIntakeLabel(status: IntakeFormStatus | null): string | null {
  return status ? INTAKE_LABEL[status] : null;
}

export function salesDeskLotCardTitle(unit: Pick<SalesDeskLotUnit, "display_label" | "model">): string {
  const model = unit.model.trim();
  return model || unit.display_label;
}

export function salesDeskLotShowsSerial(unit: Pick<SalesDeskLotUnit, "serial" | "display_label" | "model">): string | null {
  const serial = unit.serial?.trim() ?? "";
  if (!serial) return null;
  if (serial.toLowerCase() === salesDeskLotCardTitle(unit).trim().toLowerCase()) return null;
  return serial;
}

export function recordSalesDeskShown(events: SalesDeskShownEvent[], unitId: string, at: string): SalesDeskShownEvent[] {
  return [...events, { unitId, at }];
}

export function salesDeskShownCount(events: SalesDeskShownEvent[], unitId?: string): number {
  if (!unitId) return events.length;
  return events.filter((event) => event.unitId === unitId).length;
}

export function filterSalesDeskLot(units: SalesDeskLotUnit[], query: string): SalesDeskLotUnit[] {
  const q = query.trim().toLowerCase();
  if (!q) return units;
  return units.filter((unit) => {
    const haystack = [
      unit.display_label,
      unit.model,
      unit.serial ?? "",
      salesDeskLotOriginLabel(unit.origin),
      unit.notes,
      unit.salePathLabel,
      salesDeskLotIntakeLabel(unit.intake_form_status) ?? "",
    ]
      .join(" ")
      .toLowerCase();
    return haystack.includes(q);
  });
}
